/**
 * useSubscription.js
 * Minimal custom hook for subscription/entitlements fetch operations
 * 
 * STRANGLER PATTERN: This hook ONLY handles entitlements fetch + caching.
 * All side effects (tester overrides, checkout flows, gating logic, trial handling)
 * remain in App.js and are called via orchestrator functions.
 * 
 * Exposes:
 * - entitlements: The current entitlements data object
 * - tier: Current user tier ('free', 'smart', 'genius')
 * - entitlementsLoading: Boolean indicating if fetch is in progress
 * - entitlementsError: Error message if fetch failed
 * - entitlementsReady: Boolean indicating if initial load completed
 * - entitlementSource: String indicating source of current tier
 * - refreshEntitlements: Function to fetch entitlements from backend
 * - clearEntitlements: Function to clear state (for logout)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import debug from '../utils/debug';
import { logApiEvent, updateEntitlementsState } from '../utils/iosCompat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for subscription/entitlements fetch operations only
 * @returns {object} Entitlements state and functions
 */
export const useSubscription = () => {
  // State
  const [entitlements, setEntitlements] = useState(null);
  const [tier, setTier] = useState('free');
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [entitlementsError, setEntitlementsError] = useState(null);
  const [entitlementsReady, setEntitlementsReady] = useState(false);
  const [entitlementSource, setEntitlementSource] = useState(null);

  // Refs for tracking mounted state and pending calls
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const pendingCallRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Fetch user entitlements from backend
   * Returns the entitlements data for orchestrator to process side effects
   * @param {string} uid - User ID to fetch entitlements for
   * @param {object} options - { bypassCache: boolean, retryCount: number }
   * @returns {Promise<{data: object|null, success: boolean, error: string|null}>}
   */
  const refreshEntitlements = useCallback(async (uid, options = {}) => {
    const { bypassCache = false, retryCount = 0 } = options;

    // Guard: Don't call without user_id
    if (!uid) {
      debug.log('[Entitlements] No user_id provided, skipping');
      return { data: null, success: false, error: 'No user_id provided' };
    }

    // Deduplication - if a call is already pending and not bypassing cache, wait for it
    if (pendingCallRef.current && !bypassCache) {
      debug.log('[Entitlements] Call already pending, waiting...');
      return pendingCallRef.current;
    }

    // Cancel any previous request if bypassing cache
    if (bypassCache && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchPromise = (async () => {
      try {
        if (isMountedRef.current) {
          setEntitlementsLoading(true);
          setEntitlementsError(null);
        }

        const response = await fetch(
          `${BACKEND_URL}/api/entitlements/${uid}`,
          { signal: abortControllerRef.current.signal }
        );

        // Log API event for diagnostics
        logApiEvent({
          endpoint: `/api/entitlements/${uid}`,
          method: 'GET',
          status: response.status
        });

        if (!isMountedRef.current) {
          return { data: null, success: false, error: 'Component unmounted' };
        }

        if (response.ok) {
          const data = await response.json();
          
          // Update state
          setEntitlements(data);
          
          // Set tier from entitlements response (backend handles precedence)
          const effectiveTier = data.tier || 'free';
          setTier(effectiveTier);
          
          // Track entitlement source for debugging
          setEntitlementSource(data.entitlement_source || null);
          
          // Mark as ready
          setEntitlementsReady(true);
          
          // Update global diagnostics state
          updateEntitlementsState({
            loaded: true,
            tier: effectiveTier,
            tierName: data.tier_name,
            isTester: data.is_tester,
            status: data.subscription?.status
          });

          debug.log('[Entitlements] Loaded:', effectiveTier, 
            'source:', data.entitlement_source,
            'isTester:', data.is_tester,
            'testerOverride:', data.tester_override_active);

          return { data, success: true, error: null };
        } else {
          // Retry once on failure (iOS timing issue)
          if (retryCount === 0) {
            debug.log('[Entitlements] First attempt failed, retrying in 500ms...');
            pendingCallRef.current = null; // Clear so retry can proceed
            
            // Schedule retry
            return new Promise((resolve) => {
              setTimeout(async () => {
                const result = await refreshEntitlements(uid, { bypassCache: true, retryCount: 1 });
                resolve(result);
              }, 500);
            });
          } else {
            const errorMsg = `HTTP ${response.status}`;
            if (isMountedRef.current) {
              setEntitlementsError(errorMsg);
            }
            updateEntitlementsState({ loaded: false, error: errorMsg });
            return { data: null, success: false, error: errorMsg };
          }
        }
      } catch (error) {
        // Don't log abort errors
        if (error.name === 'AbortError') {
          return { data: null, success: false, error: 'Request aborted' };
        }

        console.error('Error loading entitlements:', error);
        logApiEvent({
          endpoint: `/api/entitlements/${uid}`,
          method: 'GET',
          status: 0,
          error: error.message
        });

        // Retry once on network error
        if (retryCount === 0) {
          debug.log('[Entitlements] Network error, retrying in 500ms...');
          pendingCallRef.current = null; // Clear so retry can proceed
          
          // Schedule retry
          return new Promise((resolve) => {
            setTimeout(async () => {
              const result = await refreshEntitlements(uid, { bypassCache: true, retryCount: 1 });
              resolve(result);
            }, 500);
          });
        } else {
          if (isMountedRef.current) {
            setEntitlementsError(error.message);
          }
          updateEntitlementsState({ loaded: false, error: error.message });
          return { data: null, success: false, error: error.message };
        }
      } finally {
        if (isMountedRef.current) {
          setEntitlementsLoading(false);
        }
        // Clear pending call when complete
        pendingCallRef.current = null;
      }
    })();

    // Track pending call
    pendingCallRef.current = fetchPromise;
    return fetchPromise;
  }, []);

  /**
   * Clear entitlements state (for logout)
   */
  const clearEntitlements = useCallback(() => {
    if (isMountedRef.current) {
      setEntitlements(null);
      setTier('free');
      setEntitlementsLoading(false);
      setEntitlementsError(null);
      setEntitlementsReady(false);
      setEntitlementSource(null);
    }
  }, []);

  /**
   * Clear pending call ref (for manual cache invalidation from App.js)
   */
  const clearPendingCall = useCallback(() => {
    pendingCallRef.current = null;
  }, []);

  return {
    // State
    entitlements,
    tier,
    entitlementsLoading,
    entitlementsError,
    entitlementsReady,
    entitlementSource,
    
    // Functions
    refreshEntitlements,
    clearEntitlements,
    clearPendingCall,
    
    // Direct setters for App.js orchestration (tester overrides, etc.)
    setEntitlements,
    setTier,
    setEntitlementsReady
  };
};

export default useSubscription;
